import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Settings, Check, Gauge, Globe2 } from "lucide-react";
import { LANGS, setManualLanguage } from "@/i18n";
import { useSettings } from "@/context/SettingsContext";

export const SettingsMenu = () => {
  const { t, i18n } = useTranslation();
  const { dataSaver, setDataSaver } = useSettings();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = i18n.language?.split("-")[0] || "en";

  return (
    <div className="relative" ref={ref}>
      <button data-testid="settings-btn" onClick={() => setOpen((o) => !o)} title={t("settings.title")}
        className="w-9 h-9 grid place-items-center rounded-full bg-white/10 text-zinc-300 hover:bg-white/20 transition-colors">
        <Settings size={16} />
      </button>
      {open && (
        <div data-testid="settings-menu" dir="auto"
          className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-64 bg-card border border-white/10 rounded-xl shadow-2xl p-3 z-[70]">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2 px-1">
            <Globe2 size={13} /> {t("settings.language")}
          </div>
          <div className="grid gap-1 mb-3">
            {LANGS.map((l) => (
              <button key={l.code} data-testid={`lang-opt-${l.code}`} onClick={() => { setManualLanguage(l.code); setOpen(false); }}
                className={`flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-colors ${current === l.code ? "bg-brand/20 text-white" : "text-zinc-300 hover:bg-white/10"}`}>
                <span>{l.native} <span className="text-zinc-500 text-xs">· {l.label}</span></span>
                {current === l.code && <Check size={15} className="text-brand" />}
              </button>
            ))}
          </div>
          <div className="border-t border-white/10 pt-3">
            <button data-testid="datasaver-toggle" onClick={() => setDataSaver(!dataSaver)}
              className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/10">
              <span className="flex items-center gap-2"><Gauge size={15} /> {t("settings.dataSaver")}</span>
              <span className={`w-9 h-5 rounded-full p-0.5 transition-colors ${dataSaver ? "bg-brand" : "bg-white/20"}`}>
                <span className={`block w-4 h-4 rounded-full bg-white transition-transform ${dataSaver ? "translate-x-4" : ""}`} />
              </span>
            </button>
            <p className="text-[11px] text-zinc-500 px-2.5 mt-1">{t("settings.dataSaverHint")}</p>
          </div>
        </div>
      )}
    </div>
  );
};
